import React, { useEffect, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { motion } from 'framer-motion';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  TreeDeciduous,
  Heart,
  Receipt,
  ExternalLink,
  Loader2,
  Shield
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

const EXPENSE_COLORS = {
  seedlings: '#2D5A3D',
  labor: '#40916C',
  tools: '#C9A227',
  transport: '#74A892',
  admin: '#1B4332'
};

export default function TransparencyPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransparencyData();
  }, []);

  const fetchTransparencyData = async () => {
    try {
      const res = await fetch(`${API}/transparency`);
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch transparency data:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const getCategoryLabel = (category) => {
    return t(`transparency.categories.${category}`) || category;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="transparency-page">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
          </div>
        </main>
      </div>
    );
  }

  const expenseChartData = data?.summary?.expense_by_category 
    ? Object.entries(data.summary.expense_by_category).map(([name, value]) => ({
        name: getCategoryLabel(name),
        value,
        color: EXPENSE_COLORS[name] || '#888'
      }))
    : [];

  const treeStatsData = [
    { name: 'Total', value: data?.summary?.total_trees || 0 },
    { name: 'Adopted', value: data?.summary?.adopted_trees || 0 },
    { name: 'Planted', value: data?.summary?.planted_trees || 0 },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="transparency-page">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Shield className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="transparency-title">
              {t('transparency.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('transparency.subtitle')}
            </p>
          </motion.div>

          {/* Summary Cards */}
          <motion.div 
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('transparency.totalDonated')}</p>
                    <p className="text-2xl font-bold">{formatCurrency(data?.summary?.total_donated || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('transparency.totalSpent')}</p>
                    <p className="text-2xl font-bold">{formatCurrency(data?.summary?.total_spent || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('transparency.balance')}</p>
                    <p className="text-2xl font-bold">{formatCurrency(data?.summary?.balance || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <TreeDeciduous className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('landing.treesAdopted')}</p>
                    <p className="text-2xl font-bold">{data?.summary?.adopted_trees || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Charts Row */}
          <motion.div 
            className="grid lg:grid-cols-2 gap-6 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {/* Expense Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Expense Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {expenseChartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={expenseChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {expenseChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-4 mt-4">
                      {expenseChartData.map((item) => (
                        <div key={item.name} className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-sm">{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No expenses recorded yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tree Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Tree Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={treeStatsData}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#2D5A3D" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Transactions Tables */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Tabs defaultValue="donations" className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2 mx-auto mb-6">
                <TabsTrigger value="donations" data-testid="donations-tab">
                  <Heart className="w-4 h-4 mr-2" />
                  {t('transparency.donations')}
                </TabsTrigger>
                <TabsTrigger value="expenses" data-testid="expenses-tab">
                  <Receipt className="w-4 h-4 mr-2" />
                  {t('transparency.expenses')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="donations">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('transparency.recentDonations')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {data?.donations?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>ID</TableHead>
                              <TableHead>Donor</TableHead>
                              <TableHead>{t('transparency.amount')}</TableHead>
                              <TableHead>Tree</TableHead>
                              <TableHead>{t('transparency.date')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.donations.map((donation) => (
                              <TableRow key={donation.donation_id}>
                                <TableCell className="font-mono text-xs">
                                  {donation.donation_id}
                                </TableCell>
                                <TableCell>{donation.donor_name}</TableCell>
                                <TableCell className="font-semibold">
                                  {formatCurrency(donation.amount, donation.currency)}
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                  {donation.tree_id || '-'}
                                </TableCell>
                                <TableCell>{formatDate(donation.created_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        No donations yet. Be the first to adopt a tree!
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="expenses">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('transparency.recentExpenses')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {data?.expenses?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>ID</TableHead>
                              <TableHead>{t('transparency.category')}</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead>{t('transparency.amount')}</TableHead>
                              <TableHead>{t('transparency.receipt')}</TableHead>
                              <TableHead>{t('transparency.date')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.expenses.map((expense) => (
                              <TableRow key={expense.expense_id}>
                                <TableCell className="font-mono text-xs">
                                  {expense.expense_id}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="secondary">
                                    {getCategoryLabel(expense.category)}
                                  </Badge>
                                </TableCell>
                                <TableCell>{expense.description}</TableCell>
                                <TableCell className="font-semibold">
                                  {formatCurrency(expense.amount, expense.currency)}
                                </TableCell>
                                <TableCell>
                                  {expense.receipt_url ? (
                                    <a 
                                      href={expense.receipt_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline inline-flex items-center gap-1"
                                    >
                                      View <ExternalLink className="w-3 h-3" />
                                    </a>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>{formatDate(expense.created_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        No expenses recorded yet.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
